import dayjs from 'dayjs';
import {Pencil, Share2} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Badge} from '@/components/ui/badge';
import {IconButton} from '@/components/ui/icon-button';
import type {PaperProps} from '@/components/ui/paper';
import {Paper} from '@/components/ui/paper';
import {TooltipWrapper} from '@/components/ui/tooltip';
import {Typography} from '@/components/ui/typography';
import {useAuthorize} from '@/hooks/useAuth';
import type {EventType, Resident, User} from '@/services/graphqlHooks';
import {EventSubType} from '@/services/graphqlHooks';
import ResidentAvatar from '@/shared/residents/components/ResidentAvatar';
import UserAvatar from '@/shared/users/components/UserAvatar';
import eventSubTypes from '@/translation/eventSubTypes';
import eventTypes from '@/translation/eventTypes';
import {TIME_FORMATS} from '@/utils/timeFormats';
import {cn} from '@/utils/ui';

import {
  getEventSubTypeClasses,
  getEventTypeClasses,
  getEventTypeIcon,
} from '../utils/eventsHelpers';

export type EventListItemProps = {
  id: string;
  date: string;
  type: string;
  eventSubType: string;
  content: string;
  pairingId: string;
  resident?: Resident;
  user?: User;
  isSharedToAuthorizedRelative?: boolean;
  paperProps?: Omit<PaperProps, 'children'>;
  readOnly?: boolean;
};

const EventListItem = ({
  id,
  date,
  type,
  eventSubType,
  content,
  resident,
  user,
  isSharedToAuthorizedRelative,
  paperProps,
  readOnly,
}: EventListItemProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {t} = useTranslation();

  const canUpdateEvent = useAuthorize('UPDATE_EVENT');

  const hasBadge = (eventSubType as EventSubType) !== EventSubType.None;
  const typeClasses = getEventTypeClasses(type as EventType);

  const badgesNode = (
    <>
      {hasBadge && (
        <TooltipWrapper title={eventTypes[type as EventType]}>
          <span>
            <Badge
              variant="outlined"
              color={
                getEventSubTypeClasses(eventSubType as EventSubType).badgeColor
              }
            >
              {eventSubTypes[eventSubType as EventSubType] || eventSubType}
            </Badge>
          </span>
        </TooltipWrapper>
      )}
      {!hasBadge && (
        <Badge
          variant="outlined"
          color={typeClasses.badgeColor}
          className={typeClasses.badgeClassName}
        >
          {eventTypes[type as EventType]}
        </Badge>
      )}
    </>
  );

  const editButton = canUpdateEvent && !readOnly && (
    <TooltipWrapper title={t('Edit event')}>
      <IconButton
        variant="secondary"
        onClick={() => {
          searchParams.append('editEvent', id);
          setSearchParams(searchParams);
        }}
        aria-label={t('Edit event')}
      >
        <Pencil />
        <span className="sr-only">{t('Edit event')}</span>
      </IconButton>
    </TooltipWrapper>
  );

  return (
    <div className="relative flex gap-2">
      <div className="flex flex-col items-center">
        <TooltipWrapper title={eventTypes[type as EventType]}>
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full p-2 text-white',
              getEventTypeClasses(type as EventType).icon
            )}
          >
            {getEventTypeIcon(type as EventType)}
          </div>
        </TooltipWrapper>
        <div className="my-3 min-h-4 w-px grow bg-secondary-700" />
      </div>

      <div className="flex grow flex-col gap-3 pt-3">
        <div className="flex items-center gap-2 text-xs text-neutral-800">
          {dayjs(date).format(TIME_FORMATS.dateTime)}
          {isSharedToAuthorizedRelative && canUpdateEvent && (
            <TooltipWrapper title={t('Shared with contact person')}>
              <Share2 size={16} className="text-secondary" />
            </TooltipWrapper>
          )}
        </div>

        <Paper
          {...paperProps}
          className={cn('flex flex-col gap-2', paperProps?.className)}
        >
          {resident && (
            <>
              <div className="flex items-center justify-between gap-2">
                <ResidentAvatar resident={resident} size="small" />
                {editButton}
              </div>
              <div className="self-start">{badgesNode}</div>
            </>
          )}
          {!resident && (
            <div className="flex items-center justify-between gap-2">
              {badgesNode}
              {editButton}
            </div>
          )}

          <Typography variant="default" className="text-sm text-gray-900">
            {content}
          </Typography>

          {!!user && (
            <div className="flex flex-row flex-wrap items-center gap-2">
              <Typography className="text-sm text-nowrap">
                {t('Created by')}:
              </Typography>
              <UserAvatar user={user} size="small" />
            </div>
          )}
        </Paper>
      </div>
    </div>
  );
};

export default EventListItem;
